import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  query,
  where,
  collection,
  getDocs,
  runTransaction,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import { Image } from "office-ui-fabric-react";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { selectorSrcmProfile } from "gatsby-plugin-hfn-profile/state/selectors";
import { db } from "../common1/firebase";
import { selectorSettings } from "../common1/firebase-selectors/settings";
import Logo from "../assets/hfn-logo-black.svg";

const ExcelReaderPage = () => {
  const { amIAdmin } = useSelector(selectorSettings);
  const srcmProfile = useSelector(selectorSrcmProfile);
  const [logs, setLogs] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [summary, setSummary] = useState({
    adminsUpdated: 0,
    trainersUpdated: 0,
    notFound: 0,
    errors: 0,
  });

  const addLog = (log, counterKey) => {
    setLogs((prev) => [...prev, { id: crypto.randomUUID(), ...log }]);
    if (counterKey) {
      setSummary((prev) => ({
        ...prev,
        [counterKey]: prev[counterKey] + 1,
      }));
    }
  };

  const updateCareUsersTrainerEmail = async (oldEmail, newEmail) => {
    addLog({
      result: `Checking whether the old email (${oldEmail}) is assigned to any CareUser (patients).`,
    });

    const q = query(
      collection(db, "CareUsers"),
      where("caretrainers", "array-contains", oldEmail)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    addLog({
      result: `The old email (${oldEmail}) is assigned to one or more CareUsers, and replacement with the new email (${newEmail}) is in progress.`,
    });

    const updates = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const updatedCareTrainers = data.caretrainers.map((email) =>
        email === oldEmail ? newEmail : email
      );

      return updateDoc(doc(db, "CareUsers", docSnap.id), {
        caretrainers: updatedCareTrainers,
      });
    });

    await Promise.all(updates);

    addLog({
      result: `CareUsers updated successfully. Old email (${oldEmail}) has been replaced with the new email (${newEmail}).`,
    });
  };

  const migrateSettings = async (oldEmail, newEmail) => {
    addLog({
      result: `Checking whether the old email (${oldEmail}) exists in CareSettings.`,
    });

    const settingsRef = doc(db, "CareSettings", "config");
    const snap = await getDoc(settingsRef);

    if (!snap.exists()) {
      addLog({
        result: `CareSettings configuration document not found.`,
      });
      return;
    }

    const data = snap.data();

    const updatedMap = { ...data.trainersMap };
    if (!data.trainersMap?.[oldEmail]) {
      addLog({
        result: `Old email (${oldEmail}) not found in CareSettings tainersMap.`,
      });
    } else {
      updatedMap[newEmail] = updatedMap[oldEmail];
      delete updatedMap[oldEmail];
    }

    const updatedAdminMap = { ...data.admins };
    if (!data.admins?.[oldEmail]) {
      addLog({
        result: `Old email (${oldEmail}) not found in CareSettings admins.`,
      });
    } else {
      updatedAdminMap[newEmail] = updatedAdminMap[oldEmail];
      delete updatedAdminMap[oldEmail];
    }

    addLog({
      result: `Old email (${oldEmail}) found in CareSettings. Replacing it with the new email (${newEmail}). Processing…`,
    });

    await updateDoc(settingsRef, {
      trainersMap: updatedMap,
      admins: updatedAdminMap,
    });

    addLog({
      result: `CareSettings updated successfully. Old email (${oldEmail}) has been replaced with the new email (${newEmail}).`,
    });
  };

  const processRow = async ({ old_email, new_email }) => {
    if (!old_email || !new_email) return;

    addLog({
      result: `Searching for the old Email ID in CareAdmins: ${old_email}`,
    });

    try {
      addLog({ result: "Processing…" });

      // ---- Admin check
      const adminSnap = await getDocs(
        query(collection(db, "CareAdmins"), where("email", "==", old_email))
      );

      if (!adminSnap.empty) {
        addLog({
          result:
            "Old Email ID exists in CareAdmin.\nReplacing the old Email ID with the new Email ID in CareAdmin.\nProcessing…",
        });

        const docSnap = adminSnap.docs[0];
        const newRef = doc(db, "CareAdmins", new_email.replaceAll("/", "_"));

        await runTransaction(db, async (tx) => {
          tx.set(newRef, { ...docSnap.data(), email: new_email });
          tx.delete(docSnap.ref);
        });

        addLog(
          {
            result: `CareAdmin old email (${old_email}) has been replaced with the new email (${new_email}).`,
          },
          "adminsUpdated"
        );

        await migrateSettings(old_email, new_email);
        return;
      }
      addLog({
        result: `Old Email ID (${old_email}) not found in CareAdmins.`,
      });

      // ---- Trainer check
      addLog({
        result: `Searching for the old Email ID in CareTrainers: ${old_email}. Processing…`,
      });

      const trainerSnap = await getDocs(
        query(collection(db, "CareTrainers"), where("email", "==", old_email))
      );

      if (!trainerSnap.empty) {
        addLog({
          result:
            `Old Email ID (${old_email}) exists in CareTrainers.\n` +
            `Replacing the old Email ID (${old_email}) with the new Email ID (${new_email}) in CareTrainers.\nProcessing…`,
        });

        const docSnap = trainerSnap.docs[0];
        const newRef = doc(db, "CareTrainers", new_email.replaceAll("/", "_"));

        await runTransaction(db, async (tx) => {
          tx.set(newRef, { ...docSnap.data(), email: new_email });
          tx.delete(docSnap.ref);
        });

        await updateCareUsersTrainerEmail(old_email, new_email);

        addLog(
          {
            result: `CareTrainers old email (${old_email}) has been replaced with the new email (${new_email}).`,
          },
          "trainersUpdated"
        );

        await migrateSettings(old_email, new_email);
        return;
      }
      addLog({
        result: `Old Email ID (${old_email}) not found in CareTrainers.`,
      });
    } catch (err) {
      addLog(
        {
          result: "An error occurred while processing the record.",
          message: err.message,
        },
        "errors"
      );
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isXlsx =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");
    if (!isXlsx) {
      toast.error("Please upload a valid .xlsx file only.", {
        position: "top-right",
        delay: 1000,
      });
      e.target.value = ""; // reset input
      return;
    }

    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setProcessing(true);
      setLogs([]);
      setSummary({
        adminsUpdated: 0,
        trainersUpdated: 0,
        notFound: 0,
        errors: 0,
      });

      try {
        const workbook = XLSX.read(target.result, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);

        await Promise.all(rows.map(processRow));
      } finally {
        setProcessing(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <main style={{ padding: 24 }}>
      <ToastContainer />

      {!amIAdmin && srcmProfile && (
        <div className="no-access-container">
          <Image src={Logo} width="250px" />
          <h3 className="no-access-message">
            You don&apos;t have permission to perform this action.
          </h3>
        </div>
      )}
      {!srcmProfile && (
        <div className="no-access-container">
          <Image src={Logo} width="250px" />
          <h3 className="no-access-message">
            Please login to access the application.
          </h3>
        </div>
      )}

      {amIAdmin && (
        <>
          <h1>Excel Email Migration</h1>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />

          {processing && <p>Processing records...</p>}

          <ul>
            {logs.map((log) => (
              <li key={log.id}>
                <strong style={{ whiteSpace: "pre-line" }}>{log.result}</strong>
              </li>
            ))}
          </ul>

          <h3>Summary</h3>
          <ul>
            <li>Admins updated: {summary.adminsUpdated}</li>
            <li>Trainers updated: {summary.trainersUpdated}</li>
          </ul>
        </>
      )}
    </main>
  );
};

export default ExcelReaderPage;
